import { useEffect, useRef, useMemo } from 'react';
import { Article, TypingStats, TypingState } from '../types';
import { isPunctuation } from '../utils/textUtils';

interface TypingAreaProps {
  article: Article | null;
  state: TypingState;
  stats: TypingStats;
  setInputRef: (index: number, el: HTMLInputElement | null) => void;
  onLineInput: (lineIndex: number, input: string) => void;
  onReset: () => void;
}

export function TypingArea({
  article,
  state,
  stats,
  setInputRef,
  onLineInput,
  onReset,
}: TypingAreaProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && !state.isFinished) {
      const activeInput = containerRef.current.querySelector('.line-input.active');
      if (activeInput) {
        (activeInput as HTMLElement).focus();
      }
    }
  }, [state.currentLineIndex, state.isFinished]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      onReset();
    }
  };

  if (!article) {
    return (
      <div className="typing-area">
        <div className="no-article">请选择一个文章开始练习</div>
      </div>
    );
  }

  // Pre-calculate character statuses for all lines
  const lineCharStatuses = useMemo(() => {
    return state.lines.map((line, lineIndex) => {
      const statuses: ('correct' | 'error' | 'current' | 'pending' | 'skipped')[] = [];

      if (lineIndex < state.currentLineIndex) {
        // Completed lines - punctuation is skipped (gray), others are correct
        for (let i = 0; i < line.target.length; i++) {
          if (isPunctuation(line.target[i])) {
            statuses.push('skipped');
          } else {
            statuses.push('correct');
          }
        }
      } else if (lineIndex === state.currentLineIndex) {
        // Current line - map input to target, skipping punctuation
        // 使用 rawInput（用户实际输入）来计算状态
        const rawInput = line.rawInput;
        let inputIndex = 0;
        let currentTypableIndex = -1; // 当前应该输入的可打字字符位置

        // 第一遍：找到当前应该输入的位置（跳过所有标点）
        for (let i = 0; i < line.target.length; i++) {
          const char = line.target[i];
          if (!isPunctuation(char)) {
            if (inputIndex === rawInput.length) {
              currentTypableIndex = i;
              break;
            }
            inputIndex++;
          }
        }

        // 第二遍：生成状态
        inputIndex = 0;
        for (let i = 0; i < line.target.length; i++) {
          const targetChar = line.target[i];

          if (isPunctuation(targetChar)) {
            // 标点符号永远显示为 skipped（灰色），不显示光标
            statuses.push('skipped');
          } else {
            if (inputIndex < rawInput.length) {
              // 已输入的字符 - 大小写不敏感比较
              const inputChar = rawInput[inputIndex];
              const isCorrect = inputChar.toLowerCase() === targetChar.toLowerCase();
              statuses.push(isCorrect ? 'correct' : 'error');
              inputIndex++;
            } else if (i === currentTypableIndex) {
              // 这是下一个需要输入的字符，显示光标
              statuses.push('current');
            } else {
              // 未输入的字符
              statuses.push('pending');
            }
          }
        }
      } else {
        // Future lines - punctuation is skipped (gray), others are pending
        for (let i = 0; i < line.target.length; i++) {
          if (isPunctuation(line.target[i])) {
            statuses.push('skipped');
          } else {
            statuses.push('pending');
          }
        }
      }

      return statuses;
    });
  }, [state.lines, state.currentLineIndex]);

  return (
    <div className="typing-area" ref={containerRef}>
      <div className="target-text">
        {state.lines.map((line, lineIndex) => (
          <div key={lineIndex} className="line-container">
            <div className="line-target">
              {line.target.split('').map((char, charIndex) => {
                const status = lineCharStatuses[lineIndex]?.[charIndex] ?? 'pending';
                return (
                  <span key={charIndex} className={`char char-${status}`}>
                    {char}
                  </span>
                );
              })}
            </div>
            <input
              ref={(el) => setInputRef(lineIndex, el)}
              type="text"
              className={`line-input ${lineIndex === state.currentLineIndex && !state.isFinished ? 'active' : ''}`}
              value={line.rawInput}
              onChange={(e) => onLineInput(lineIndex, e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={lineIndex !== state.currentLineIndex || state.isFinished}
              placeholder={lineIndex === state.currentLineIndex && !state.isFinished ? '在此输入...' : ''}
              autoFocus={lineIndex === state.currentLineIndex}
            />
          </div>
        ))}
      </div>

      {state.isFinished && (
        <div className="result-overlay">
          <div className="result-content">
            <h3>🎉 练习完成！</h3>
            <div className="result-stats">
              <div className="result-stat">
                <span className="stat-value">{stats.wpm}</span>
                <span className="stat-label">WPM</span>
              </div>
              <div className="result-stat">
                <span className="stat-value">{stats.accuracy}%</span>
                <span className="stat-label">准确率</span>
              </div>
              <div className="result-stat">
                <span className="stat-value">{stats.errorCount}</span>
                <span className="stat-label">错误数</span>
              </div>
            </div>
            <button className="btn-primary" onClick={onReset}>
              再试一次 (Tab)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
